import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  applyBatchResultsToSaveStates,
  cellKey,
  computeFailedSaves,
  splitSaveTargetsBySavingKeys,
} from './spreadsheetSelection'
import type {
  SpreadsheetCellSaveState,
  SpreadsheetEditableCell,
  SpreadsheetEditableMeta,
  SpreadsheetSaveBatchItem,
} from './spreadsheetTypes'

interface SaveTarget<TRow> {
  cell: SpreadsheetEditableCell<TRow>
  index: number
  key: string
  value: string
}

export function useSpreadsheetSaveQueue<TRow>() {
  const savingCellKeysRef = useRef(new Set<string>())
  const queuedTargetsRef = useRef(new Map<string, SaveTarget<TRow>>())
  const [pendingSaveCount, setPendingSaveCount] = useState(0)
  const [saveStates, setSaveStates] = useState<
    Record<string, SpreadsheetCellSaveState>
  >({})

  const runSaveBatch = useCallback(async function runSaveBatch(
    targets: SaveTarget<TRow>[]
  ) {
    if (targets.length === 0) return

    const savingKeys = targets.map((target) => target.key)
    savingKeys.forEach((key) => savingCellKeysRef.current.add(key))
    setPendingSaveCount((prev) => prev + savingKeys.length)

    setSaveStates((prev) => {
      const next = { ...prev }
      targets.forEach(({ key, value }) => {
        next[key] = { status: 'saving', optimisticValue: value }
      })
      return next
    })

    const results = await Promise.allSettled(
      targets.map(({ cell, value }) => cell.meta.saveValue(cell.row, value))
    )
    const queuedNextByKey = new Map<string, SaveTarget<TRow>>()
    targets.forEach((target) => {
      const queued = queuedTargetsRef.current.get(target.key)
      if (!queued) return
      queuedTargetsRef.current.delete(target.key)
      queuedNextByKey.set(target.key, queued)
    })
    const queuedNextKeys = new Set(queuedNextByKey.keys())
    const failed = computeFailedSaves(targets, results, queuedNextKeys)

    setSaveStates((prev) =>
      applyBatchResultsToSaveStates(prev, targets, queuedNextByKey, failed)
    )

    savingKeys.forEach((key) => savingCellKeysRef.current.delete(key))
    setPendingSaveCount((prev) => Math.max(0, prev - savingKeys.length))

    const callbackBatches = new Map<
      NonNullable<SpreadsheetEditableMeta<TRow>['afterSaveBatch']>,
      SpreadsheetSaveBatchItem<TRow>[]
    >()
    targets.forEach(({ cell, value }, index) => {
      const callback = cell.meta.afterSaveBatch
      if (!callback) return

      const result = results[index]
      const item: SpreadsheetSaveBatchItem<TRow> =
        result?.status === 'rejected'
          ? {
              row: cell.row,
              value,
              status: 'rejected',
              reason: result.reason,
            }
          : { row: cell.row, value, status: 'fulfilled' }

      callbackBatches.set(callback, [
        ...(callbackBatches.get(callback) ?? []),
        item,
      ])
    })
    callbackBatches.forEach((items, callback) => callback(items))

    if (failed.length > 0) {
      toast.error(
        `${failed.length} selected cell${
          failed.length === 1 ? '' : 's'
        } failed to save.`,
        { toastId: 'spreadsheet-save-error' }
      )
    }

    if (queuedNextByKey.size > 0) {
      void runSaveBatch(Array.from(queuedNextByKey.values()))
    }
  }, [])

  const saveCells = useCallback(
    async (
      cells: SpreadsheetEditableCell<TRow>[],
      valueForCell: (
        cell: SpreadsheetEditableCell<TRow>,
        index: number
      ) => string
    ) => {
      if (cells.length === 0) return

      const targets = cells.map((cell, index) => ({
        cell,
        index,
        key: cellKey(cell.coordinate),
        value: valueForCell(cell, index),
      }))
      const { readyTargets, queuedTargets } = splitSaveTargetsBySavingKeys(
        targets,
        savingCellKeysRef.current
      )

      if (queuedTargets.length > 0) {
        queuedTargets.forEach((target) => {
          queuedTargetsRef.current.set(target.key, target)
        })
        setSaveStates((prev) => {
          const next = { ...prev }
          queuedTargets.forEach(({ key, value }) => {
            next[key] = { status: 'saving', optimisticValue: value }
          })
          return next
        })
      }

      await runSaveBatch(readyTargets)
    },
    [runSaveBatch]
  )

  return {
    pendingSaveCount,
    saveCells,
    saveStates,
  }
}
